import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode
} from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import {
  getDraftVersionDocx,
  listDraftVersions,
  restoreDraftVersion
} from "../../../api/drafts-v2";
import { Draft, DraftVersion } from "../../../api/types/draft";
import type { DraftDocxEditorProps } from "../../draftEditor/DraftDocxEditor";
import { DraftVersionHistoryDrawer } from "./DraftVersionHistoryDrawer";

const VERSIONS_PAGE_SIZE = 10;

const showVersionToast = ({
  variant,
  title,
  description,
  durationMs = 3500
}: {
  variant: "brand" | "neutral" | "error" | "success";
  title: string;
  description: string;
  durationMs?: number;
}) => {
  const options = { description, duration: durationMs };
  if (variant === "error") toast.error(title, options);
  else if (variant === "neutral") toast.info(title, options);
  else toast.success(title, options);
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftId: string | null;
  draftTitle: string;
  /** Called after a successful restore with the restored draft. */
  onRestored?: (restored: Draft) => void;
  /** Overlay for the version preview editor (see `DraftDocxEditor.overlay`). */
  editorOverlay?: DraftDocxEditorProps["overlay"];
  /**
   * When given, "Restore" loads the version's bytes into the open editor
   * instead of overwriting the server copy; the editor's save (autosave in
   * the draft pane) keeps it as a new version, conflict check included.
   */
  onRestoreUnsaved?: (
    bytes: ArrayBuffer,
    version: DraftVersion
  ) => Promise<void>;
  /** The live document's bytes, for "Compare with current". */
  currentBytes?: () => Promise<ArrayBuffer | null>;
};

/**
 * Self-contained version history experience (drawer + restore confirm dialog).
 * Manages its own version fetching/pagination and restore flow given a draftId.
 */
export const DraftVersionHistory = ({
  open,
  onOpenChange,
  draftId,
  draftTitle,
  onRestored,
  editorOverlay,
  onRestoreUnsaved,
  currentBytes
}: Props) => {
  const [versions, setVersions] = useState<DraftVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLoadingMoreVersions, setIsLoadingMoreVersions] = useState(false);
  const [hasMoreVersions, setHasMoreVersions] = useState(false);
  const [versioningError, setVersioningError] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(
    null
  );
  const [pendingRestoreVersion, setPendingRestoreVersion] =
    useState<DraftVersion | null>(null);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);

  const versionsCountRef = useRef(0);
  const isLoadingVersionsRef = useRef(false);
  const isLoadingMoreVersionsRef = useRef(false);

  useEffect(() => {
    versionsCountRef.current = versions.length;
  }, [versions.length]);

  const normalizeVersions = useCallback(
    (items: DraftVersion[]): DraftVersion[] => {
      return items
        .map((item, idx) => {
          const record = item as unknown as Record<string, unknown>;
          const rawVersionId =
            typeof record.version_id === "string"
              ? record.version_id
              : typeof record.id === "string"
              ? record.id
              : null;
          const rawDraftId =
            typeof record.draft_id === "string" ? record.draft_id : draftId;

          const version_id = rawVersionId ?? `${draftId ?? "draft"}:${idx}`;
          const draft_id = rawDraftId ?? draftId ?? "";

          return { ...record, version_id, draft_id } as DraftVersion;
        })
        .filter((v) => Boolean(v.version_id));
    },
    [draftId]
  );

  const fetchVersions = useCallback(
    async (opts?: { append?: boolean }) => {
      if (!draftId) return;
      const append = Boolean(opts?.append);

      if (append) {
        if (isLoadingMoreVersionsRef.current || isLoadingVersionsRef.current)
          return;
        isLoadingMoreVersionsRef.current = true;
        setIsLoadingMoreVersions(true);
      } else {
        isLoadingVersionsRef.current = true;
        setIsLoadingVersions(true);
        setVersioningError(null);
        setHasMoreVersions(false);
      }

      try {
        const offset = append ? versionsCountRef.current : 0;
        const raw = await listDraftVersions(draftId, {
          limit: VERSIONS_PAGE_SIZE,
          offset
        });
        const normalized = normalizeVersions(raw);

        setHasMoreVersions(raw.length === VERSIONS_PAGE_SIZE);
        setVersions((prev) => {
          if (!append) return normalized;

          const seen = new Set(prev.map((v) => v.version_id));
          const next = [...prev];
          normalized.forEach((v) => {
            if (!seen.has(v.version_id)) {
              seen.add(v.version_id);
              next.push(v);
            }
          });
          return next;
        });
      } catch (e) {
        Sentry.captureException(e);
        if (!append) {
          setVersioningError("Failed to load version history.");
        } else {
          showVersionToast({
            variant: "error",
            title: "Couldn't load more versions",
            description: "Please try again.",
            durationMs: 4000
          });
        }
      } finally {
        if (append) {
          isLoadingMoreVersionsRef.current = false;
          setIsLoadingMoreVersions(false);
        } else {
          isLoadingVersionsRef.current = false;
          setIsLoadingVersions(false);
        }
      }
    },
    [draftId, normalizeVersions]
  );

  useEffect(() => {
    if (!open) return;
    fetchVersions().catch(() => {});
  }, [open, fetchVersions]);

  const handleRestoreClick = useCallback((version: DraftVersion) => {
    setPendingRestoreVersion(version);
    setIsRestoreConfirmOpen(true);
  }, []);

  const performRestore = useCallback(async () => {
    if (!draftId || !pendingRestoreVersion?.version_id) return;

    const versionId = pendingRestoreVersion.version_id;
    setRestoringVersionId(versionId);
    try {
      if (onRestoreUnsaved) {
        // Through the open editor, so its save carries the revision check
        // rather than overwriting the server copy here.
        const { bytes } = await getDraftVersionDocx(draftId, versionId);
        await onRestoreUnsaved(bytes, pendingRestoreVersion);
        showVersionToast({
          variant: "brand",
          title: "Version restored",
          description:
            "It saves as a new version; the text it replaced stays in history."
        });
        setIsRestoreConfirmOpen(false);
        setPendingRestoreVersion(null);
        onOpenChange(false);
        return;
      }

      const restored = await restoreDraftVersion(draftId, versionId);

      showVersionToast({
        variant: "brand",
        title: "Version restored",
        description: "This draft has been restored to the selected version.",
        durationMs: 4000
      });

      setIsRestoreConfirmOpen(false);
      setPendingRestoreVersion(null);

      await fetchVersions();
      onOpenChange(false);
      onRestored?.(restored);
    } catch (e) {
      Sentry.captureException(e);
      showVersionToast({
        variant: "error",
        title: "Restore failed",
        description: "We couldn't restore that version. Please try again.",
        durationMs: 5000
      });
    } finally {
      setRestoringVersionId(null);
    }
  }, [
    draftId,
    fetchVersions,
    onOpenChange,
    onRestoreUnsaved,
    onRestored,
    pendingRestoreVersion
  ]);

  return (
    <>
      <DraftVersionHistoryDrawer
        open={open}
        onOpenChange={onOpenChange}
        draftId={draftId}
        draftTitle={draftTitle}
        versions={versions}
        isLoading={isLoadingVersions}
        isLoadingMore={isLoadingMoreVersions}
        hasMore={hasMoreVersions}
        onLoadMore={() => {
          fetchVersions({ append: true }).catch(() => {});
        }}
        error={versioningError}
        restoringVersionId={restoringVersionId}
        onRestoreClick={handleRestoreClick}
        editorOverlay={editorOverlay}
        currentBytes={currentBytes}
      />

      <AlertDialog
        open={isRestoreConfirmOpen}
        onOpenChange={(next) => {
          if (!next) setIsRestoreConfirmOpen(false);
        }}
      >
        <AlertDialogContent
          className="z-2147483647"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Restore This Version?</AlertDialogTitle>
            <AlertDialogDescription>
              {onRestoreUnsaved
                ? "It replaces what is in the editor and saves as a new version. The current text stays in version history."
                : "Restoring will replace the current draft content. You can restore another version later if needed."}
            </AlertDialogDescription>
            {pendingRestoreVersion?.created_at ? (
              <p className="text-sm text-fg-3">
                Selected version created{" "}
                {new Date(pendingRestoreVersion.created_at).toLocaleString()}.
              </p>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              variant="secondary"
              disabled={Boolean(restoringVersionId)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="primary"
              disabled={Boolean(restoringVersionId)}
              onClick={(e) => {
                e.preventDefault();
                performRestore().catch(() => {});
              }}
            >
              {restoringVersionId ? <Spinner className="size-3.5" /> : null}
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DraftVersionHistory;
